import { AnyAction } from '@reduxjs/toolkit';
import * as icons from 'icons';

import type { DialogProps } from 'common/components/Dialog/Dialog';
import type { ApiExceptionMessage } from 'modules/api/utils';

export enum AlertSeverity {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

export enum AlertType {
  BackendError = 'backendError',
  Dialog = 'dialog',
  SnackBar = 'snackbar',
}

export type AlertContext = 'APP' | 'BROADCAST' | 'NATTERING';

export type AppAlertMessage = {
  message: string;
  metadata?: unknown;
  error?: Error;
  timeout?: number;
  icon?: keyof typeof icons;
  action?: AnyAction;
  actionLabel?: string;
};

export type AlertBaseData = {
  id: string;
  type: AlertType;
  context: AlertContext;
};

export type SnackBarAlertData = AlertBaseData & {
  type: AlertType.SnackBar;
  message: string | AppAlertMessage;
  severity: AlertSeverity;
};

export type BackendErrorAlertData = AlertBaseData & {
  type: AlertType.BackendError;
  message: string | AppAlertMessage | ApiExceptionMessage;
};

export type DialogAlertData = AlertBaseData & {
  type: AlertType.Dialog;
  message: string;
  // `initialFocus` holds a ref or a function, which has no place in Redux state.
  options: Partial<Omit<DialogProps, 'onClose' | 'onConfirm' | 'initialFocus'>> &
    Required<Pick<DialogProps, 'title' | 'description'>> & {
      actionToDispatchOnConfirm?: AnyAction;
      actionToDispatchOnClose?: AnyAction;
    };
};

export type AlertData = SnackBarAlertData | BackendErrorAlertData | DialogAlertData;

export const isSnackBarAlert = (data: AlertData): data is SnackBarAlertData => data.type === AlertType.SnackBar;
export const isBackendErrorAlert = (data: AlertData): data is BackendErrorAlertData =>
  data.type === AlertType.BackendError;
export const isDialogAlert = (data: AlertData): data is DialogAlertData => data.type === AlertType.Dialog;
export const isAlertMatchingContext = (context: string) => (data: AlertData) => data.context === context;
